import { apiGet, apiPost } from './client';

/**
 * Phase 109 (Plan 109-08 / SLACK-SEARCH-03, SLACK-SEARCH-05, SLACK-SEARCH-06)
 * — frontend API client for the session-authed Slack search connect surface
 * built in Plan 109-06 (`backend/src/api/schemas/slackSearch.ts`). Every
 * interface field below mirrors the backend response schema EXACTLY —
 * fast-json-stringify strips anything not enumerated there, so there is no
 * unused field to type here (see `botConnection.ts`'s header for the same
 * rule, first established in Phase 73).
 *
 * D-02: Monksheets brokers the search; the Slack `xoxp` token never reaches
 * the frontend. It is deliberately absent from every shape in this file and
 * must never be added — that absence is SLACK-SEARCH-05's proof on this
 * side of the boundary.
 */

// Mirrors `slackSearchStatusResponse` in backend/src/api/schemas/slackSearch.ts.
// `data` (and therefore the resolved value) is `null` for a caller with no
// `bot_connections` row at all — a legitimate value, never coerced away.
export interface SlackSearchStatus {
  connected: boolean;
  connectedAt: string | null;
  revokedAt: string | null;
  scopes: string[];
  redirectUri: string;
}

// Mirrors `slackSearchDisconnectResponse`. `outcome` lets the UI be honest
// (D-10 / WR-12) about exactly what happened: 'revoked' (Slack confirmed),
// 'revoke_failed' (Slack itself was unreachable/rejected — the local
// credential was still deleted), or 'nothing_to_revoke' (never connected,
// or an idempotent second disconnect — NOT a failure). `success` alone
// would over-report what happened, and a single boolean (the pre-WR-12
// shape) could not distinguish the last two states — the bug WR-12 fixed.
export type SlackSearchRevokeOutcome = 'revoked' | 'revoke_failed' | 'nothing_to_revoke';

export interface SlackSearchDisconnectResult {
  success: boolean;
  outcome: SlackSearchRevokeOutcome;
}

export async function getSlackSearchStatus(): Promise<SlackSearchStatus | null> {
  const res = await apiGet<{ data: SlackSearchStatus | null }>('/bot/slack-search/status');
  return res.data;
}

export async function disconnectSlackSearch(): Promise<SlackSearchDisconnectResult> {
  const res = await apiPost<{ data: SlackSearchDisconnectResult }>(
    '/bot/slack-search/disconnect',
    {}
  );
  return res.data;
}

/**
 * Builds the connect URL for a full-page redirect — see the comment on the
 * connect button in `MonkBotSection.tsx` for why this must stay
 * `window.location.href`, never a `fetch`. Resolves the api base the same
 * way `authContext.tsx`'s `login()` does.
 *
 * The path is a hard-coded literal appended to that base; this function
 * takes no arguments and there is no caller-suppliable value baked into the
 * result. That is why the WR-04 open-redirect guard `login()` needs (reject
 * a path that isn't a single leading slash) does not apply here — there is
 * nothing for an attacker to poison. Do not "fix" this asymmetry by adding
 * a parameter.
 */
export function slackSearchConnectUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  return `${apiUrl}/bot/slack-search/connect`;
}
